import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import Header from '../Web/components/header';
import Footer from '../Web/components/footer';
import { getAuth } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const DashboardLayout = ({ children }) => {
  const [isOpen, setIsOpen] = useState(window.innerWidth < 768 ? false : true);
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const menuItems = [
    { title: 'Getting Started', path: '/dashboard', icon: '🚀' },
    { title: 'About iFLO Pro', path: '/dashboard/about', icon: 'ℹ️' },
    { title: 'Install Guide', path: '/dashboard/install', icon: '📖' },
    { title: 'FAQs', path: '/dashboard/faqs', icon: '❓' },
    { title: 'News', path: '/dashboard/news', icon: '📰' },
    { title: 'Education Center', path: '/dashboard/education', icon: '🎓' },
    { title: 'Store', path: '/dashboard/store', icon: '🛍️' },
    { title: 'Account', path: '/dashboard/account', icon: '👤' },
    { title: 'Contact', path: '/dashboard/contact', icon: '📞' },
    { title: 'Home', path: '/', icon: '🏠' },
  ];

  const { user } = useAuth();
  const auth = getAuth();

  //console.log(user);

  return (
    <>
    {/*<Header />*/}
    <div className="flex bg-white">
      {/* Sidebar */}
      <motion.div 
        initial={{ x: -300 }}
        animate={{ x: 0 }}
        className={`${
          isOpen ? 'md:w-64 w-full overflow-hidden md:relative absolute z-50 left-0 top-0 min-h-screen shadow-lg' : 'w-0 hidden md:block md:w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 ease-in-out`}
      >
        <div className="p-4">
          <motion.img 
            src="https://cdn.shopify.com/s/files/1/0738/7290/0321/files/iFLO_PRO_Logo.png?v=1742928906" 
            alt="iFLO Pro"
            onClick={() => {
              navigate('/');
              setIsOpen(false);
            }}
            className="h-12 mx-auto object-contain cursor-pointer"
            whileHover={{ scale: 1.05 }}
          />
        </div>

        <nav className="mt-8">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path}>
              <motion.div
                whileHover={{ x: 10, backgroundColor: '#fef3c7' }}
                onClick={() => {
                  if (window.innerWidth < 768) {
                    setIsOpen(false);
                  }
                }}
                className={`flex items-center px-6 py-3 text-gray-700 ${
                  location.pathname === item.path ? 'bg-yellow-50 border-r-4 border-yellow-500' : ''
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isOpen ? 1 : 0 }}
                  className="ml-4 font-medium"
                >
                  {item.title}
                </motion.span>
              </motion.div>
            </Link>
          ))}

          <div className="absolute top-4 right-4 md:hidden">
            <button 
              onClick={() => setIsOpen(false)} 
              className="flex items-center justify-center text-black p-2 rounded-full transition-colors"
              aria-label="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </nav>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <motion.header 
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          className="bg-white border-b border-gray-200 p-4"
        >
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </motion.button>

            <div className="flex items-center space-x-4">
              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <input
                  type="search"
                  placeholder="Search..."
                  className="px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    const query = e.target.value.toLowerCase();
                    const matchingItems = menuItems.filter(item => 
                      item.title.toLowerCase().includes(query)
                    );
                    if (matchingItems.length > 0 && matchingItems[0].path !== '/') {
                      navigate(matchingItems[0].path);
                    }
                  }}
                />
                {searchQuery && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {menuItems
                      .filter(item => item.title.toLowerCase().includes(searchQuery.toLowerCase()))
                      .map(item => (
                        <Link 
                          key={item.path} 
                          to={item.path}
                          className="block px-4 py-2 hover:bg-yellow-50"
                          onClick={() => setSearchQuery('')}
                        >
                          <div className="flex items-center">
                            <span className="text-xl mr-2">{item.icon}</span>
                            <span>{item.title}</span>
                          </div>
                        </Link>
                      ))}
                  </div>
                )}
              </motion.div>

              <motion.div 
                whileHover={{ scale: 1.05 }}
                className="flex items-center cursor-pointer space-x-2"
                onClick={() => navigate('/dashboard/account')}
              >
                <img
                  src={user?.photoURL || '/logos/user.png'}
                  alt="Profile"
                  className="w-10 h-10 rounded-full"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '';
                  }}
                />
                <span className="font-medium text-gray-700">{user?.displayName || 'User'}</span> 
              </motion.div>
            </div>
          </div>
        </motion.header>

        {/* Page Content */}
        <motion.main 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="p-6"
        >
          {children}
        </motion.main>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default DashboardLayout; 